import { env } from "@/lib/env";

/** Runtime SSO configuration from `GET /public/auth/sso` (System → Single sign-on). */
export interface SsoConfig {
  enabled: boolean;
  /** GoTrue `/authorize?provider=azure`; the panel appends `redirect_to`. */
  authorizeUrl: string;
}

export const SSO_OFF: SsoConfig = { enabled: false, authorizeUrl: "" };

export async function fetchSsoConfig(fetchImpl: typeof fetch = fetch): Promise<SsoConfig> {
  try {
    const r = await fetchImpl(`${env.apiUrl}/api/v1/public/auth/sso`, {
      headers: { accept: "application/json" },
      signal: AbortSignal.timeout(6000),
    });
    if (!r.ok) return SSO_OFF;
    const j = (await r.json()) as { enabled?: boolean; authorize_url?: string };
    return j.enabled && j.authorize_url
      ? { enabled: true, authorizeUrl: j.authorize_url }
      : SSO_OFF;
  } catch {
    // API unreachable at boot: the login page still works with email + password.
    return SSO_OFF;
  }
}

/** Where Microsoft sign-in sends the browser: GoTrue's authorize endpoint, back to /login. */
export function microsoftSignInUrl(cfg: SsoConfig, origin = window.location.origin): string {
  const u = new URL(cfg.authorizeUrl);
  u.searchParams.set("redirect_to", `${origin}/login`);
  return u.toString();
}

/** GoTrue returns to `/login#access_token=…` (or `#error=…&error_description=…`). */
export function parseSsoCallback(hash: string): { accessToken: string } | { error: string } | null {
  const p = new URLSearchParams(hash.replace(/^#/, ""));
  const token = p.get("access_token");
  if (token) return { accessToken: token };
  const err = p.get("error_description") || p.get("error");
  if (err) return { error: err };
  return null;
}
